// @ts-nocheck - verbatim port from my-location-next/utils (plain JS, not authored to TS strict). Type it if/when reworked.
// Geo math for routes. Pure functions; no side effects, no DOM.

const EARTH_RADIUS_M = 6371008.8; // WGS-84 mean radius

function toRad(deg) { return deg * Math.PI / 180; }

/** Great-circle distance (meters) between two lat/lng points. */
export function distanceMeters(a, b) {
  if (!a || !b) return 0;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/** Total length (meters) of a polyline defined by waypoints. */
export function routeDistanceMeters(waypoints) {
  if (!waypoints || waypoints.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < waypoints.length; i++) {
    total += distanceMeters(waypoints[i - 1], waypoints[i]);
  }
  return total;
}

// Units chosen in Settings: a function returning true (imperial), false (metric)
// or null (follow the browser). Set by stores/settings.svelte.js; a getter, so a
// template that formats a distance updates when the setting changes.
let imperialSource = () => null;
/** @param {() => boolean | null} source */
export function setImperialSource(source) {
  imperialSource = typeof source === 'function' ? source : () => null;
}
/** The Settings choice, else miles for en-US / en-LR / en-MM. */
export function prefersImperial() {
  const chosen = imperialSource();
  if (typeof chosen === 'boolean') return chosen;
  return typeof navigator !== 'undefined' && /^en-(US|LR|MM)/i.test(navigator.language || '');
}

/**
 * Format meters as a short display string. Uses the Settings choice, else the
 * locale's measurement system heuristic from navigator.language; defaults to metric.
 *   1234.5  → "1.23 km"
 *   234.5   → "235 m"
 *   8 047   → "5.0 mi" (en-US)
 */
/** @param {number} meters @param {{imperial?: boolean | null}} [options] */
export function formatDistance(meters, { imperial = null } = {}) {
  const useImperial = imperial != null ? imperial : prefersImperial();
  if (useImperial) {
    const miles = meters / 1609.344;
    if (miles < 0.1) {
      const feet = Math.round(meters * 3.28084);
      return `${feet} ft`;
    }
    return `${miles.toFixed(miles < 10 ? 2 : 1)} mi`;
  }
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10_000 ? 2 : 1)} km`;
}

// Optional activity labels for a sketch route. Kept in sync with the Worker's
// ROUTE_ACTIVITIES allowlist (place.js) — anything outside this set is dropped.
export const ROUTE_ACTIVITIES = Object.freeze(['walk', 'run', 'hike', 'bike', 'drive']);

// The account keeps at most this many routes in sync (the Worker's entity limit).
export const ROUTE_LIMIT = 50;
export const ROUTE_LIMIT_WARN_AT = 45;

// Colours offered for a route line. Green and red are left out because they mark
// the first and last point while drawing; blue is the default line colour.
// `key` names the label (routes.color.<key>).
export const ROUTE_COLORS = Object.freeze([
  { key: 'orange', hex: '#F97316' },
  { key: 'violet', hex: '#7C3AED' },
  { key: 'pink', hex: '#DB2777' },
  { key: 'teal', hex: '#0D9488' },
  { key: 'gray', hex: '#4B5563' },
]);

/**
 * Text key for a counted noun: `<base>.one`, `<base>.other`, ... following the
 * plural rules of `locale`. The caller falls back to `<base>.other` when the
 * catalog has no entry for the category.
 */
export function pluralKey(base, count, locale) {
  let category = 'other';
  try {
    category = new Intl.PluralRules(locale || 'en').select(Number(count) || 0);
  } catch {
    /* unknown locale: keep "other" */
  }
  return `${base}.${category}`;
}

/** Newest first, so a route that was just drawn or changed is at the top. */
export function sortRoutesNewestFirst(routes) {
  const stamp = (r) => Number(r?.updatedAt) || Number(r?.createdAt) || 0;
  return [...(routes || [])].sort((a, b) => stamp(b) - stamp(a));
}

// Squared distance from point p to segment a→b, in lng-corrected lat/lng units
// (good enough for sketch-scale routes; avoids a full projection).
function pointToSegmentSq(p, a, b) {
  const k = Math.cos((a.lat * Math.PI) / 180) || 1;
  const px = p.lng * k, py = p.lat;
  const ax = a.lng * k, ay = a.lat;
  const bx = b.lng * k, by = b.lat;
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx, cy = ay + t * dy;
  return (px - cx) ** 2 + (py - cy) ** 2;
}

/**
 * Index of the waypoint AFTER which a clicked point should be inserted — the
 * start index of the segment nearest to `pt`. Returns 0 for fewer than 2 points.
 */
export function nearestSegmentIndex(waypoints, pt) {
  if (!waypoints || waypoints.length < 2) return 0;
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const d = pointToSegmentSq(pt, waypoints[i], waypoints[i + 1]);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** New random UUID for client-generated IDs. */
export function newRouteId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'r_' + Math.random().toString(36).slice(2);
}

export function newWaypointId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'w_' + Math.random().toString(36).slice(2);
}
